/**
 * Policy clauses rendered from the manifest (policyManifest.ts §5): for every clause the build
 * describes that the hand-built form does not ask about, a section drawn from its node in the
 * manifest's JSON Schema — objects as fieldsets, booleans as toggles, enums as selects, lists as
 * chips, bounded numbers as number inputs, records as key/value rows, anything else as JSON
 * checked against the node. A clause marked `requiresPlatform` says what the platform provides.
 */

import { checkPolicyAgainstSchema, FOUNDRY_ORG_SECRET_NAMES, type ManifestClause, type PolicyManifest } from '@aioe/shared';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { computeRoutes, foundryRoutes, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, inputClass, StatusMark, Tick } from './ui.tsx';
import { FORM_CLAUSES, PolicyGroupHeading, PolicySection } from './policyEditor.tsx';

/** A ListBox id for the empty answer (none, any, all): an empty string is not a reliable id. */
const NONE = '__none';

type Schema = Record<string, unknown>;
/** The manifest's `fields`: what the workbench calls each part of the policy, by dotted path (absent before D319). */
const FieldLabels = createContext<PolicyManifest['fields']>(undefined);
type Raw = Record<string, unknown>;
/**
 * Fields something else keeps, by dotted path: shown read only with a note and the way to where they are changed, in place
 * of their editor. The foundry clause's `projects` is the Foundry connections' while any exist (the server refuses a hand
 * edit); a save that leaves it alone sends it back as it was.
 */
const ManagedFields = createContext<Record<string, (value: unknown) => ReactNode>>({});

const get = (doc: Raw, path: string): unknown => path.split('.').reduce<unknown>((n, seg) => (n && typeof n === 'object' && !Array.isArray(n) ? (n as Raw)[seg] : undefined), doc);
function set(doc: Raw, path: string, value: unknown): Raw {
  const segs = path.split('.');
  const next: Raw = { ...doc };
  let node: Raw = next;
  for (const seg of segs.slice(0, -1)) {
    const child = node[seg];
    node[seg] = child && typeof child === 'object' && !Array.isArray(child) ? { ...(child as Raw) } : {};
    node = node[seg] as Raw;
  }
  const last = segs.at(-1)!;
  if (value === undefined) delete node[last];
  else node[last] = value;
  return next;
}
const schemaAt = (root: Schema, path: string): Schema | undefined => path.split('.').reduce<Schema | undefined>((s, seg) => (s?.properties && typeof s.properties === 'object' ? ((s.properties as Record<string, Schema>)[seg] ?? undefined) : undefined), root);
const title = (key: string) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
const semver = (a: string, b: string) => {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  return 0;
};

/**
 * Clauses whose platform feature this organisation serves, with where it is looked after in the console: the section says
 * so instead of what it needs. `microapps` is not marked as a whole (its `publishing` is), so it is named here too.
 */
const SERVED_HERE: Record<string, string> = {
  microapps: 'Published apps are listed, reviewed and withdrawn under Apps and routines; an approval withdrawn there is added to the revoked apps here.',
  routines: 'Published routines are listed, reviewed and withdrawn under Apps and routines.',
};

/**
 * The clauses the hand-built form already asks about, and those the platform writes itself (`exceptions`, D379: approved
 * per request under Exceptions, never on a policy): the manifest fills in the rest.
 */
export const HAND_BUILT = new Set<string>(['version', 'source', 'settings', 'settingsMode', 'exceptions', ...FORM_CLAUSES]);

export function ManifestClauseSections({ manifest, raw, onChange, oldestVersion }: { manifest: PolicyManifest; raw: Raw; onChange: (next: Raw) => void; oldestVersion?: string }) {
  const groups = manifest.ui.groups.filter((g) => g.kind === 'policy').sort((a, b) => a.order - b.order);
  const clauses = manifest.policy.clauses.filter((c) => !c.path.includes('.') && !HAND_BUILT.has(c.path));
  if (!clauses.length) return null;
  const byGroup = new Map<string, ManifestClause[]>();
  for (const c of clauses) byGroup.set(c.group, [...(byGroup.get(c.group) ?? []), c]);
  return (
    <FieldLabels.Provider value={manifest.fields}>
      {groups
        .filter((g) => byGroup.has(g.id))
        .map((g) => (
          <div key={g.id}>
            <PolicyGroupHeading>{g.title}</PolicyGroupHeading>
            {byGroup.get(g.id)!.map((c) => {
              const node = schemaAt(manifest.policy.jsonSchema as Schema, c.path);
              const value = get(raw, c.path);
              // The build only matters when a machine in scope is too old for the clause.
              const tooNew = oldestVersion && semver(c.since, oldestVersion) > 0;
              return (
                <PolicySection key={c.path} title={c.title} summary={value === undefined ? 'Not set: the workbench’s defaults apply' : 'Set by this policy'} aside={tooNew ? `Needs AI Workbench ${c.since} or later` : undefined}>
                  <div className="grid gap-3">
                    <p className="text-sm text-muted">{c.description}</p>
                    {SERVED_HERE[c.path] ? (
                      <p className="flex flex-wrap items-center gap-2 text-xs text-muted">
                        <StatusMark state="connected" label="Provided by this organisation" />
                        {SERVED_HERE[c.path]}
                      </p>
                    ) : c.requiresPlatform ? (
                      <p className="text-xs text-warning">Needs the platform: {c.platformFeature ?? 'a feature behind it'}.</p>
                    ) : null}
                    {c.path === 'foundry' ? <FoundryOrgSecrets /> : null}
                    <ManagedHere path={c.path}>
                      {node ? <Field schema={node} value={value} path={c.path} depth={0} onChange={(v) => onChange(set(raw, c.path, v))} /> : <JsonField schema={{}} value={value} onChange={(v) => onChange(set(raw, c.path, v))} />}
                    </ManagedHere>
                    {value !== undefined ? (
                      <div>
                        <Button size="sm" variant="ghost" onPress={() => onChange(set(raw, c.path, undefined))}>
                          Leave to the workbench
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </PolicySection>
              );
            })}
          </div>
        ))}
    </FieldLabels.Provider>
  );
}

/** Marks the fields of a clause that another part of the console keeps (today the foundry clause's projects, while Foundry connections exist). */
function ManagedHere({ path, children }: { path: string; children: ReactNode }) {
  const connections = useLoad(() => (path === 'foundry' ? foundryRoutes.connections().catch(() => undefined) : Promise.resolve(undefined)), [path]);
  const any = (connections.data?.connections.length ?? 0) > 0;
  const approved = useLoad(() => (path === 'compute' ? computeRoutes.clusters().catch(() => undefined) : Promise.resolve(undefined)), [path]);
  const anyClusters = (approved.data?.clusters.length ?? 0) > 0;
  const managed = useMemo<Record<string, (value: unknown) => ReactNode>>(
    () =>
      (any
        ? {
            // What the connections approve, not what this document holds: it may not have been rewritten yet.
            'foundry.projects': () => {
              const names = (connections.data?.connections ?? []).flatMap((c) => (c.kind === 'azure' ? (c.projects ?? []).map((p) => p.name) : [`${c.azureLocal?.cluster ?? c.name} (Azure Local)`]));
              return (
                <div className="grid gap-1 rounded-lg bg-surface-secondary p-3 text-sm">
                  <span className="font-medium">Managed by Foundry connections</span>
                  <span className="text-xs text-muted">{names.length ? `Approved now: ${names.join(', ')}.` : 'Nothing is approved yet.'} Add, change or remove projects there and this list follows.</span>
                  <Link className="w-fit text-xs text-accent hover:underline" to="/foundry">
                    Open Foundry connections
                  </Link>
                </div>
              );
            },
          }
        : anyClusters
          ? {
              // The approved clusters, not what this document holds: it may not have been rewritten yet.
              'compute.clusters': () => (
                <div className="grid gap-1 rounded-lg bg-surface-secondary p-3 text-sm">
                  <span className="font-medium">Managed by approved clusters</span>
                  <span className="text-xs text-muted">Approved now: {(approved.data?.clusters ?? []).map((c) => c.name).join(', ')}. Add, change or remove clusters there and this list follows.</span>
                  <Link className="w-fit text-xs text-accent hover:underline" to="/compute">
                    Open Compute
                  </Link>
                </div>
              ),
            }
          : {}) as Record<string, (value: unknown) => ReactNode>,
    [any, anyClusters, connections.data, approved.data],
  );
  return <ManagedFields.Provider value={managed}>{children}</ManagedFields.Provider>;
}

/** The foundry clause's organisation identity: the three organisation secrets nodes publish with, and whether all are kept (names only, never values). */
function FoundryOrgSecrets() {
  const list = useLoad(() => routes.secrets(), []);
  const kept = new Set((list.data?.secrets ?? []).filter((s) => s.scope === 'organisation').map((s) => s.name));
  const missing = FOUNDRY_ORG_SECRET_NAMES.filter((n) => !kept.has(n));
  return (
    <div className="grid gap-1">
      <p className="text-sm text-muted">Nodes publish as the organisation with the secrets FOUNDRY_TENANT_ID, FOUNDRY_CLIENT_ID and FOUNDRY_CLIENT_SECRET, kept under Secrets.</p>
      {list.data ? <StatusMark state={missing.length ? 'medium' : 'connected'} label={missing.length ? `Not kept yet: ${missing.join(', ')}` : 'All three are kept'} /> : null}
    </div>
  );
}

function Field({ schema, value, path, depth, onChange }: { schema: Schema; value: unknown; path: string; depth: number; onChange: (v: unknown) => void }): ReactNode {
  const type = Array.isArray(schema.type) ? (schema.type as string[])[0] : (schema.type as string | undefined);
  const enumValues = Array.isArray(schema.enum) ? (schema.enum as unknown[]) : undefined;
  const label = useContext(FieldLabels)?.[path]?.label ?? title(path.split('.').at(-1) ?? path);
  const help = typeof schema.description === 'string' ? schema.description : undefined;
  const placeholder = schema.default !== undefined ? `default ${JSON.stringify(schema.default)}` : 'not set';
  const managed = useContext(ManagedFields)[path];
  if (managed) return <Row label={label} help={help}>{managed(value)}</Row>;

  if (enumValues && type !== 'array') {
    return (
      <Row label={label} help={help}>
        <ChoiceSelect
          label={label}
          value={value === undefined ? NONE : String(value)}
          options={{ [NONE]: placeholder, ...Object.fromEntries(enumValues.map((x) => [String(x), String(x)])) }}
          onChange={(v) => onChange(v === NONE ? undefined : enumValues.find((x) => String(x) === v))}
        />
      </Row>
    );
  }
  if (type === 'boolean') {
    return (
      <Row label={label} help={help}>
        <ChoiceSelect label={label} value={value === undefined ? NONE : String(value)} options={{ [NONE]: placeholder, true: 'On', false: 'Off' }} onChange={(v) => onChange(v === NONE ? undefined : v === 'true')} />
      </Row>
    );
  }
  if (type === 'integer' || type === 'number') {
    return (
      <Row label={label} help={help}>
        <input type="number" className={`${inputClass} max-w-[10rem]`} aria-label={label} value={typeof value === 'number' ? value : ''} min={typeof schema.minimum === 'number' ? schema.minimum : undefined} max={typeof schema.maximum === 'number' ? schema.maximum : undefined} step={type === 'integer' ? 1 : 'any'} placeholder={placeholder} onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
      </Row>
    );
  }
  if (type === 'string') {
    return (
      <Row label={label} help={help}>
        <input className={inputClass} aria-label={label} value={typeof value === 'string' ? value : ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)} />
      </Row>
    );
  }
  if (type === 'array') {
    const items = (schema.items && typeof schema.items === 'object' ? schema.items : {}) as Schema;
    const itemEnum = Array.isArray(items.enum) ? (items.enum as unknown[]) : undefined;
    const list = Array.isArray(value) ? (value as unknown[]) : [];
    if (itemEnum) {
      return (
        <Row label={label} help={help}>
          <div className="flex flex-wrap gap-2">
            {itemEnum.map((x) => {
              const on = list.includes(x);
              return (
                <Tick key={String(x)} checked={on} onChange={(v) => onChange(v ? [...list, x] : list.filter((y) => y !== x))}>
                  {String(x)}
                </Tick>
              );
            })}
          </div>
        </Row>
      );
    }
    if (items.type === 'string') return <Row label={label} help={help}><Chips items={list.map(String)} onChange={(v) => onChange(v.length ? v : undefined)} /></Row>;
    if (items.type === 'object' && items.properties && typeof items.properties === 'object' && depth < 2) {
      return (
        <Row label={label} help={help}>
          <ObjectList props={items.properties as Record<string, Schema>} list={list} path={path} depth={depth} onChange={(v) => onChange(v.length ? v : undefined)} />
        </Row>
      );
    }
    return <Row label={label} help={help}><JsonField schema={schema} value={value} onChange={onChange} /></Row>;
  }
  if (type === 'object' && schema.properties && typeof schema.properties === 'object' && depth < 2) {
    const props = schema.properties as Record<string, Schema>;
    const obj = value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : {};
    return (
      <fieldset className={depth ? 'grid gap-2 rounded-lg border border-separator p-3' : 'grid gap-2'}>
        {depth ? <legend className="px-1 text-xs text-muted">{label}</legend> : null}
        {help && depth ? <p className="text-xs text-muted">{help}</p> : null}
        {Object.entries(props).map(([k, sub]) => (
          <Field
            key={k}
            schema={sub}
            value={obj[k]}
            path={`${path}.${k}`}
            depth={depth + 1}
            onChange={(v) => {
              const next: Raw = { ...obj };
              if (v === undefined) delete next[k];
              else next[k] = v;
              onChange(Object.keys(next).length ? next : undefined);
            }}
          />
        ))}
      </fieldset>
    );
  }
  if (type === 'object' && schema.additionalProperties && typeof schema.additionalProperties === 'object' && depth < 3) {
    const obj = value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : {};
    const keyEnum = schema.propertyNames && typeof schema.propertyNames === 'object' && Array.isArray((schema.propertyNames as Schema).enum) ? ((schema.propertyNames as Schema).enum as string[]) : undefined;
    const valueSchema = schema.additionalProperties as Schema;
    return (
      <Row label={label} help={help}>
        <KeyValues obj={obj} keyEnum={keyEnum} valueSchema={valueSchema} onChange={(next) => onChange(Object.keys(next).length ? next : undefined)} />
      </Row>
    );
  }
  return (
    <Row label={label} help={help}>
      <JsonField schema={schema} value={value} onChange={onChange} />
    </Row>
  );
}

function Row({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 text-sm lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-start lg:gap-3">
      <div>
        <div className="font-medium">{label}</div>
        {help ? <div className="text-xs text-muted">{help}</div> : null}
      </div>
      <div>{children}</div>
    </div>
  );
}

export function Chips({ items, onChange }: { items: string[]; onChange: (items: string[]) => void }) {
  const [text, setText] = useState('');
  const add = () => {
    const v = text.trim();
    if (!v) return;
    onChange([...items, v]);
    setText('');
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {items.map((x, i) => (
        <span key={`${x}-${i}`} className="mono inline-flex items-center gap-1 rounded-full bg-surface-secondary px-2 py-0.5 text-xs">
          {x}
          <button type="button" className="text-muted hover:text-foreground" aria-label={`Remove ${x}`} onClick={() => onChange(items.filter((_, j) => j !== i))}>
            ×
          </button>
        </span>
      ))}
      <input className={`${inputClass} max-w-[14rem]`} value={text} placeholder="Add…" onChange={(e) => setText(e.target.value)} onKeyDown={(e) => (e.key === 'Enter' ? (e.preventDefault(), add()) : undefined)} onBlur={add} />
    </div>
  );
}

/** A list of objects (the Foundry clause's projects, each with its deployments and tools): one bordered card per entry, its fields drawn as any other. */
function ObjectList({ props, list, path, depth, onChange }: { props: Record<string, Schema>; list: unknown[]; path: string; depth: number; onChange: (next: unknown[]) => void }) {
  return (
    <div className="grid gap-2">
      {list.map((entry, i) => {
        const obj = entry && typeof entry === 'object' && !Array.isArray(entry) ? (entry as Raw) : {};
        return (
          <div key={i} className="grid gap-2 rounded-lg border border-separator p-3">
            {Object.entries(props).map(([k, sub]) => (
              <Field
                key={k}
                schema={sub}
                value={obj[k]}
                path={`${path}.${k}`}
                depth={depth + 2}
                onChange={(v) => {
                  const next: Raw = { ...obj };
                  if (v === undefined) delete next[k];
                  else next[k] = v;
                  onChange(list.map((x, j) => (j === i ? next : x)));
                }}
              />
            ))}
            <div>
              <Button size="sm" variant="ghost" onPress={() => onChange(list.filter((_, j) => j !== i))}>
                Remove
              </Button>
            </div>
          </div>
        );
      })}
      <div>
        <Button size="sm" variant="ghost" onPress={() => onChange([...list, {}])}>
          Add
        </Button>
      </div>
    </div>
  );
}

function KeyValues({ obj, keyEnum, valueSchema, onChange }: { obj: Raw; keyEnum?: string[]; valueSchema: Schema; onChange: (next: Raw) => void }) {
  const [key, setKey] = useState('');
  const keys = Object.keys(obj);
  const free = keyEnum?.filter((k) => !keys.includes(k));
  return (
    <div className="grid gap-2">
      {keys.map((k) => (
        <div key={k} className="grid gap-1 sm:grid-cols-[12rem_minmax(0,1fr)_auto] sm:items-center">
          <span className="mono text-xs">{k}</span>
          <Field schema={valueSchema} value={obj[k]} path={k} depth={3} onChange={(v) => onChange({ ...obj, [k]: v })} />
          <Button size="sm" variant="ghost" onPress={() => onChange(Object.fromEntries(keys.filter((x) => x !== k).map((x) => [x, obj[x]])))}>
            Remove
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        {keyEnum ? (
          <ChoiceSelect label="Key" value={key || undefined} placeholder="Add…" options={Object.fromEntries((free ?? []).map((k) => [k, k]))} onChange={setKey} />
        ) : (
          <input className={`${inputClass} mono max-w-[14rem]`} value={key} placeholder="key" onChange={(e) => setKey(e.target.value)} aria-label="Key" />
        )}
        <Button size="sm" variant="ghost" isDisabled={!key.trim()} onPress={() => { onChange({ ...obj, [key.trim()]: valueSchema.type === 'string' ? '' : valueSchema.type === 'array' ? [] : valueSchema.type === 'boolean' ? false : valueSchema.type === 'object' ? {} : '' }); setKey(''); }}>
          Add
        </Button>
      </div>
    </div>
  );
}

function JsonField({ schema, value, onChange }: { schema: Schema; value: unknown; onChange: (v: unknown) => void }) {
  const [text, setText] = useState(value === undefined ? '' : JSON.stringify(value, null, 2));
  const [problem, setProblem] = useState<string>();
  return (
    <div className="grid gap-1">
      <textarea
        className={`${inputClass} mono min-h-24`}
        value={text}
        spellCheck={false}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setProblem(undefined);
            onChange(undefined);
            return;
          }
          try {
            const v: unknown = JSON.parse(e.target.value);
            const r = checkPolicyAgainstSchema(v, schema);
            setProblem(r.errors[0] ? `${r.errors[0].path || 'value'}: ${r.errors[0].message}` : undefined);
            if (!r.errors.length) onChange(v);
          } catch (err) {
            setProblem((err as Error).message);
          }
        }}
      />
      {problem ? <div className="text-xs text-danger">{problem}</div> : null}
    </div>
  );
}
